const RECAPTCHA_SCRIPT_ID = 'recaptcha-v3-script';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (
        siteKey: string,
        options: { action: string }
      ) => Promise<string>;
    };
  }
}

let loadPromise: Promise<void> | null = null;

const waitForGrecaptcha = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (!window.grecaptcha) {
      reject(new Error('reCAPTCHA is not available'));
      return;
    }

    window.grecaptcha.ready(() => resolve());
  });

const loadRecaptchaScript = (siteKey: string): Promise<void> => {
  if (window.grecaptcha?.execute) {
    return waitForGrecaptcha();
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(RECAPTCHA_SCRIPT_ID);
    if (existing) {
      waitForGrecaptcha().then(resolve).catch(reject);
      return;
    }

    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.onload = () => {
      waitForGrecaptcha().then(resolve).catch(reject);
    };
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
    document.head.appendChild(script);
  });

  return loadPromise;
};

export const preloadRecaptcha = (): Promise<void> | null => {
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  if (!siteKey) {
    return null;
  }

  return loadRecaptchaScript(siteKey);
};

export const getRecaptchaToken = async (
  action: string
): Promise<string | null> => {
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  if (!siteKey) {
    return null;
  }

  await loadRecaptchaScript(siteKey);
  return window.grecaptcha!.execute(siteKey, { action });
};
